import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaDownload, FaTimes, FaCompress, FaExpand } from 'react-icons/fa';

const API_URL = '/api';

// Icon components
const BoxIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
  </svg>
);

const BarcodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
    <path d="M6 8h1v4H6V8zm2 0h1v4H8V8zm2 0h1v4h-1V8zm2 0h1v4h-1V8zm2 0h1v4h-1V8z" />
  </svg>
);

const CloseIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-${size} w-${size}`} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// Batch Info Modal Component
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BoxIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? new Date(batch.ExpiryDate || batch.expiryDate).toLocaleDateString() 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BoxIcon className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Info Modal Component
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BarcodeIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {/* Fix: Extract the SerialNumber property from the object */}
                          {serial.SerialNumber || serial || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BarcodeIcon className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IssueForProductionViewModal = ({ open, onClose, issueId }) => {
  const [issueData, setIssueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  useEffect(() => {
    if (open && issueId) {
      fetchIssueData();
      fetchScreenConfig();
    } else {
      setIssueData(null);
      setLoading(true);
      setError(null);
    }
  }, [open, issueId]);

  const fetchScreenConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`);
      const data = await response.data;
      console.log('Fetched screen configurations:', data);
      
      // Find the IssueForProduction screen configuration
      const issueForProductionScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'IssueForProduction'.toLowerCase()
      );
      
      console.log('IssueForProduction screen configuration:', issueForProductionScreen);
      if (issueForProductionScreen) {
        setScreenConfig(issueForProductionScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = issueForProductionScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = issueForProductionScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };

  const fetchIssueData = async () => {
    try {
      setLoading(true);
      // Fix: Check for both tokens like in the main component
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      // Fix: Handle both object and string ID cases
      const id = typeof issueId === 'object' ? issueId.IssueID : issueId;
      
      const response = await axios.get(`${API_URL}/issue-for-production/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setIssueData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching issue for production data:', err);
      setError('Failed to fetch issue for production data');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!issueData) return;
    
    try {
      const response = await axios.get(`${API_URL}/designs/Issue%20For%20Production/pdf/issue-for-production/${issueData.IssueID}`, {
        responseType: 'arraybuffer',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `IssueForProduction_${issueData.IssueID}.pdf`;
      link.click();
      
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    }
  };

  // Function to toggle maximize state
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };
  
  // Function to toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };

  // Functions to open batch and serial modals
  const openBatchModal = (batches, productName) => {
    setSelectedBatches(batches);
    setSelectedProductName(productName);
    setShowBatchModal(true);
  };

  const openSerialModal = (serials, productName, location) => {
    // Fix: Extract just the serial numbers from the objects
    const serialNumbers = serials.map(serial => 
      typeof serial === 'object' && serial.SerialNumber ? serial.SerialNumber : serial
    );
    
    setSelectedSerials(serialNumbers);
    setSelectedProductName(productName);
    setSelectedLocation(location);
    setShowSerialModal(true);
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${
        isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
      }`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Issue For Production Details</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadPDF}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FaDownload /> Download PDF
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <FaCompress size={20} /> : <FaExpand size={20} />}
              </button>
              <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                <FaTimes size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content - only show if not minimized */}
        {!isMinimized && (
          <>
            {/* Scrollable Content Area */}
            <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
              {loading ? (
                <div className="text-center py-8">
                  <p>Loading issue for production details...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : issueData ? (
                <div className="space-y-6">
                  {/* IssueForProduction Header */}
                  <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                    <h3 className="text-lg font-semibold text-[#384959] mb-4">Issue For Production Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Issue ID</label>
                        <input 
                          value={issueData.IssueID || ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Issue Date</label>
                        <input 
                          type="text" 
                          value={issueData.IssueDate ? new Date(issueData.IssueDate).toLocaleDateString() : ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Production Order ID</label>
                        <input 
                          value={issueData.ProductionOrderID || ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Production Order Qty</label>
                        <input 
                          value={issueData.ProductionOrderQty || 0} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Issued By User ID</label>
                        <input 
                          value={issueData.IssuedByUserID || ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Issued By User Name</label>
                        <input 
                          value={issueData.IssuedByUserName || ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                        <input 
                          value={`${issueData.BranchCode || ''} - ${issueData.BranchName || ''}`} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                      
                      {/* Department field - Only shown if ShowOnHeader is true */}
                      {showDepartmentField && (
                        <div className="flex flex-col gap-2">
                          <label className="block text-sm font-medium text-[#64748b]">Department</label>
                          <input 
                            value={`${issueData.DepartmentCode || ''} - ${issueData.DepartmentName || ''}`} 
                            readOnly
                            className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                          />
                        </div>
                      )}
                      
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Status</label>
                        <input 
                          value={issueData.Status || ''} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Inventory Flag</label>
                        <input 
                          value={issueData.InventoryFlag ? 'Yes' : 'No'} 
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* IssueForProduction Details */}
                  <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-[#384959]">Issue For Production Details</h3>
                    </div>
                    
                    {issueData.Details && issueData.Details.length > 0 ? (
                      <>
                        <div className="overflow-x-auto custom-scrollbar">
                          <table className="min-w-full border border-[#E2E8F0]">
                            <thead>
                              <tr className="bg-[#BDDDFC] text-[#384959]">
                                <th className="p-2">#</th>
                                <th className="p-2">Product Name</th>
                                <th className="p-2">Product Code</th>
                                <th className="p-2">Material Type</th>
                                <th className="p-2">UOM</th>
                                <th className="p-2">Qty</th>
                                <th className="p-2">Production Order Qty</th>
                                <th className="p-2">Rate</th>
                                <th className="p-2">Value</th>
                                <th className="p-2">Flush Type</th>
                                <th className="p-2">Warehouse</th>
                                {/* Department column - Only shown if ShowOnItem is true */}
                                {showDepartmentItemField && <th className="p-2">Department</th>}
                                <th className="p-2">Tracking</th>
                              </tr>
                            </thead>
                            <tbody>
                              {issueData.Details.map((detail, idx) => (
                                <tr key={idx} className="border-t border-[#E2E8F0]">
                                  <td className="p-2 text-center">{idx + 1}</td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.ProductName || ''} 
                                      readOnly
                                      className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.ProductCode || ''} 
                                      readOnly
                                      className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.MaterialType || ''} 
                                      readOnly
                                      className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.UOM || ''} 
                                      readOnly
                                      className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.Qty || 0} 
                                      readOnly
                                      className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.CalculatedQty || 0} 
                                      readOnly
                                      className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.Rate || 0} 
                                      readOnly
                                      className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.Value || 0} 
                                      readOnly
                                      className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.FlushType || ''} 
                                      readOnly
                                      className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                    />
                                  </td>
                                  <td className="p-2">
                                    <input 
                                      value={detail.Warehouse || ''} 
                                      readOnly
                                      className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                    />
                                  </td>
                                  
                                  {/* Department cell - Only shown if ShowOnItem is true */}
                                  {showDepartmentItemField && (
                                    <td className="p-2">
                                      <input 
                                        value={`${detail.DepartmentCode || ''} - ${detail.DepartmentName || ''}`} 
                                        readOnly
                                        className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                      />
                                    </td>
                                  )}
                                  
                                  <td className="p-2">
                                    {detail.Batches && detail.Batches.length > 0 ? (
                                      <button 
                                        onClick={() => openBatchModal(detail.Batches, detail.ProductName)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        View Batches ({detail.Batches.length})
                                      </button>
                                    ) : detail.Serials && detail.Serials.length > 0 ? (
                                      <button 
                                        onClick={() => openSerialModal(detail.Serials, detail.ProductName, detail.Warehouse)}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        View Serials ({detail.Serials.length})
                                      </button>
                                    ) : (
                                      <span>N/A</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Summary */}
                        <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                          <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-500">Total Qty</span>
                              <span className="text-base font-semibold">
                                {issueData.Details.reduce((sum, detail) => sum + (parseFloat(detail.Qty) || 0), 0)}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-500">Total Calculated Qty</span>
                              <span className="text-base font-semibold text-lg text-blue-600">
                                {issueData.Details.reduce((sum, detail) => sum + (parseFloat(detail.CalculatedQty) || 0), 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>No details available</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            
            {/* Fixed Footer with Buttons */}
            <div className="flex-shrink-0 p-8 pt-0">
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Batch Info Modal */}
      <BatchInfoModal 
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        batches={selectedBatches}
        productName={selectedProductName}
      />
      
      {/* Serial Info Modal */}
      <SerialInfoModal 
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        serialNumbers={selectedSerials}
        productName={selectedProductName}
        locationCode={selectedLocation}
      />
    </div>
  );
};

export default IssueForProductionViewModal;